import { SpecialAnchorTypes, ToolBaseTypes, ToolTypes } from '@labradorsports/constants';
import { arraySlidingWindow } from '@labradorsports/utils';

import FieldPiece from '../models/field-piece.js';
import FieldViewport from '../models/field-viewport.js';
import Move from '../models/move.js';
import { PluginContext } from '../plugins/api.js';

import {
    findClosestPiece,
    angleBetweenPoints,
    getMidpoint,
    getShadingCorners,
    getShadingRotationHandle,
    rotateAroundOrigin,
    snapMoveEndpointToAngleGrid,
} from './geometry.js';
import {
    defaultMove,
    calculatePartialMovement,
    findClosestPassablePoint,
    findClosestPointAlongMove,
    findClosestOptionPoint,
    getMoveLines,
    getCurvedMovePoints,
    getMoveLength,
    getPreviousMoveAnchor,
} from './moves.js';
import { createLines, resizeShading } from './play.js';

export function generatePassAnchors({ play, frame }) {
    const anchors = [];

    if (!play.fieldSetup && frame.passes) {
        frame.passes.forEach((pass) => {
            anchors.push({
                type: pass.type,
                id: pass.id,
                target: pass.target,
                ...pass.anchors[0],
                space: 'screen' as const,
            });

            anchors.push({
                type: SpecialAnchorTypes.PASS_START,
                id: pass.id,
                target: pass.target,
                ...pass.origin,
                space: 'screen' as const,
            });
        });
    }

    return anchors;
}

export function generatePassSelectedAnchors({ play, frame }, selected) {
    const anchors = [];

    if (play.config.MoveConfig[selected.type].baseType === ToolBaseTypes.PASS) {
        if (!play.fieldSetup && frame.passes) {
            frame.passes.forEach((pass) => {
                anchors.push({
                    type: pass.type,
                    id: pass.id,
                    target: pass.target,
                    ...pass.anchors[0],
                    space: 'screen' as const,
                });

                anchors.push({
                    type: SpecialAnchorTypes.PASS_START,
                    id: pass.id,
                    target: pass.target,
                    ...pass.origin,
                    space: 'screen' as const,
                });
            });
        }
    }

    return anchors;
}

/**
 * Distance along a player's move where a pass/option starts.
 * Defaults to 0, or the end of that move when defaultToMoveEnd is set.
 * When end is provided (catch point along the receiver path), start is clamped to not exceed it.
 */
export function resolveMoveStart(
    frame,
    {
        playerId,
        existingStart,
        defaultToMoveEnd,
        end,
    }: {
        playerId: number;
        existingStart?: number;
        defaultToMoveEnd?: boolean;
        end?: number;
    }
): number {
    let start = 0;

    if (typeof existingStart === 'number') {
        start = existingStart;
    } else if (defaultToMoveEnd) {
        const playerMove = frame.getMove(playerId);

        if (playerMove) {
            start = getMoveLength(playerMove);
        }
    }

    if (typeof end === 'number' && end < start) {
        return Math.max(end, 0);
    }

    return start;
}

/** Catch distance along the receiver move. Player target = start of their move when they have one. */
function resolvePassEnd(onReceiverRoute: boolean, distanceAlong: number, receiverHasMove: boolean): number | undefined {
    if (onReceiverRoute) {
        return distanceAlong;
    }

    return receiverHasMove ? 0 : undefined;
}

type MoveStartOptions = { defaultToMoveEnd?: boolean };

export function getDefaultPassMove(
    { play, lines, fieldViewport, frame },
    type,
    offset,
    options?: MoveStartOptions
) {
    const closestCarrier = findClosestPiece(offset, frame.hasBall());

    if (!closestCarrier) {
        return null;
    }

    if (frame.balls.length > 1) {
        const pass = defaultMove(frame.field, closestCarrier, type);
        pass.target = closestCarrier.id;

        return pass;
    }

    const carrierBall = frame.getBallsByCarrier(closestCarrier.id)[0];
    const { closestPassable, distanceAlong, closestMove, closestLineDistance, closestDistance } =
        findClosestPassablePoint(
            getMoveLines(play.config, lines, frame.idx),
            fieldViewport,
            frame,
            offset,
            type,
            carrierBall.id
        );

    if (!closestPassable) {
        return null;
    }

    const onReceiverRoute = closestLineDistance < closestDistance;
    const receiverMove = frame.getMove(closestPassable.id);
    const end = resolvePassEnd(onReceiverRoute, distanceAlong, Boolean(receiverMove));
    const start = resolveMoveStart(frame, {
        playerId: closestCarrier.id,
        defaultToMoveEnd: options?.defaultToMoveEnd,
        end,
    });
    const carrierMove = frame.getMove(closestCarrier.id);
    const origin = carrierMove
        ? (calculatePartialMovement(carrierMove, start) ?? closestCarrier.origin)
        : closestCarrier.origin;
    const endpoint = onReceiverRoute
        ? calculatePartialMovement(closestMove, distanceAlong)
        : closestPassable.origin;

    return new Move({
        type,
        target: closestCarrier.id,
        origin,
        anchors: [endpoint],
        targetId: closestPassable.id,
    });
}

export function handlePassDrag(
    { play, lines, api, fieldViewport, frame }: PluginContext,
    anchor: Anchor,
    offset: FieldPoint,
    options?: MoveStartOptions
) {
    const pass = frame.passes.find((p) => p.id === anchor.id);
    const defaultToMoveEnd = options?.defaultToMoveEnd;

    if (anchor.type === SpecialAnchorTypes.PASS_START) {
        const carrierMove = frame.getMove(frame.getCarrier(pass.props.ball).id);

        if (!carrierMove) {
            return;
        }

        const { distanceAlong } = findClosestPointAlongMove(carrierMove, offset);
        const carrierMoveLength = getMoveLength(carrierMove);

        const receiverMove = frame.getMove(pass.target);
        const passEnd = pass?.props?.end ?? carrierMoveLength;
        const newPassEnd =
            receiverMove && distanceAlong > passEnd ? Math.min(distanceAlong, getMoveLength(receiverMove)) : passEnd;

        frame.updatePass(anchor.id, {
            origin: calculatePartialMovement(carrierMove, distanceAlong),
            endpoint: calculatePartialMovement(receiverMove, newPassEnd),
        });
    } else {
        const {
            closestLineDistance,
            closestDistance,
            closestPoint,
            closestLine,
            closestPassable,
            distanceAlong,
            closestMove,
        } = findClosestPassablePoint(
            getMoveLines(play.config, lines, frame.idx),
            fieldViewport,
            frame,
            offset,
            anchor.type,
            pass.props.ball
        );

        api.debug.points([
            offset,
            closestPoint ? fieldViewport.getFieldCoords(closestPoint) : null,
            closestLine ? fieldViewport.getFieldCoords(closestLine.a) : null,
            closestLine ? fieldViewport.getFieldCoords(closestLine.b) : null,
            ...(closestMove?.curved ? getCurvedMovePoints(closestMove) : []),
        ]);

        const onReceiverRoute = closestLineDistance < closestDistance;
        const targetPlayer = onReceiverRoute ? closestMove.target : closestPassable?.id;
        if (typeof targetPlayer !== 'number') {
            return;
        }

        const carrier = frame.getCarrier(pass.props.ball);
        const carrierMove = frame.getMove(carrier.id);
        const receiverMove = frame.getMove(targetPlayer);
        const end = resolvePassEnd(onReceiverRoute, distanceAlong, Boolean(receiverMove));
        const start = resolveMoveStart(frame, {
            playerId: carrier.id,
            existingStart: pass?.props?.start,
            defaultToMoveEnd,
            end,
        });

        frame.updatePass(anchor.id, {
            targetId: targetPlayer,
            endpoint: onReceiverRoute
                ? calculatePartialMovement(closestMove, end)
                : closestPassable.origin,
            ...(carrierMove ? { origin: calculatePartialMovement(carrierMove, start) } : {}),
        });
    }
}

export function handlePassDragEnd(
    { api, play, lines, fieldViewport, frame }: PluginContext,
    anchor: Anchor,
    offset: FieldPoint,
    options?: MoveStartOptions
) {
    // Pass ID will use ball ID when left blank
    const closestCarrier = findClosestPiece(offset, frame.hasBall());
    const closestBalls = frame.getBallsByCarrier(closestCarrier.id);
    const pass = frame.passes?.find((p) => p.id === anchor.id);
    const defaultToMoveEnd = options?.defaultToMoveEnd;

    if (anchor.type === SpecialAnchorTypes.PASS_START) {
        const carrierMove = frame.getMove(frame.getCarrier(pass.props.ball).id);

        if (!carrierMove) {
            return;
        }

        const { distanceAlong } = findClosestPointAlongMove(carrierMove, offset);
        const receiverMove = frame.getMove(pass.target);
        const carrierMoveLength = getMoveLength(carrierMove);
        const passEnd = pass.props?.end ?? carrierMoveLength;
        const newPassEnd =
            receiverMove && distanceAlong > passEnd ? Math.min(distanceAlong, getMoveLength(receiverMove)) : passEnd;

        api.updatePass({
            id: anchor.id,
            start: distanceAlong,
            end: newPassEnd,
        });
    } else {
        // New passes
        if (typeof anchor.target === 'undefined' && frame.balls.length > 1) {
            const targetBall = closestBalls.find((ball) => !frame.passes?.some((pass) => pass.props.ball === ball.id));
            const ballId = (targetBall ?? closestBalls[0]).id;
            const carrier = frame.getCarrier(ballId);
            const start = resolveMoveStart(frame, {
                playerId: carrier.id,
                defaultToMoveEnd,
            });

            api.updatePass({
                type: anchor.type,
                ball: ballId,
                target: null,
                start,
            });
        } else {
            const commonUpdate = {
                id: anchor.id,
                ball: pass?.props?.ball ?? closestBalls[0].id,
                type: anchor.type,
            };

            const { closestLineDistance, closestDistance, distanceAlong, closestMove, closestPassable } =
                findClosestPassablePoint(
                    getMoveLines(play.config, lines, frame.idx),
                    fieldViewport,
                    frame,
                    offset,
                    anchor.type,
                    commonUpdate.ball
                );

            const onReceiverRoute = closestLineDistance < closestDistance;
            const targetPlayer = onReceiverRoute ? closestMove.target : closestPassable?.id;
            if (typeof targetPlayer !== 'number') {
                return;
            }

            const carrier = frame.getCarrier(commonUpdate.ball);
            const receiverMove = frame.getMove(targetPlayer);
            const end = resolvePassEnd(onReceiverRoute, distanceAlong, Boolean(receiverMove));
            const start = resolveMoveStart(frame, {
                playerId: carrier.id,
                existingStart: pass?.props?.start,
                defaultToMoveEnd,
                end,
            });

            api.updatePass({
                ...commonUpdate,
                target: targetPlayer,
                start,
                // Only persist catch-at-start for football; lacrosse/basketball leave end
                // unset so generatePassMove still uses the end of the receiver route.
                ...(typeof end === 'number' && (onReceiverRoute || defaultToMoveEnd)
                    ? { end }
                    : {}),
            });
        }
    }
}

export function generateOptionAnchors({ play, frame }: Pick<PluginContext, 'play' | 'frame'>) {
    const anchors = [];

    if (!play.fieldSetup && frame.options) {
        frame.options.forEach((opt) => {
            anchors.push({
                type: ToolTypes.OPTION,
                id: opt.id,
                target: opt.target,
                ...opt.anchors[0],
                space: 'screen' as const,
            });

            anchors.push({
                type: SpecialAnchorTypes.OPTION_START,
                id: opt.id,
                target: opt.target,
                ...opt.origin,
                space: 'screen' as const,
            });
        });
    }

    return anchors;
}

export function generateOptionSelectedAnchors(
    { play, frame }: Pick<PluginContext, 'play' | 'frame'>,
    selected
) {
    const anchors = [];

    if (selected?.type !== ToolTypes.OPTION || play.fieldSetup || !frame.options) {
        return anchors;
    }

    const option = frame.options.find((opt) => opt.id === selected.id);
    if (!option) {
        return anchors;
    }

    anchors.push({
        type: ToolTypes.OPTION,
        id: option.id,
        target: option.target,
        ...option.anchors[0],
        space: 'screen' as const,
    });

    anchors.push({
        type: SpecialAnchorTypes.OPTION_START,
        id: option.id,
        target: option.target,
        ...option.origin,
        space: 'screen' as const,
    });

    return anchors;
}

export function generateOptionLines({ play, frame }: Pick<PluginContext, 'play' | 'frame'>) {
    if (!play.fieldSetup && frame.options) {
        return frame.options.flatMap(createLines);
    }

    return [];
}

export function handleOptionDragEnd(
    { api, play, lines, fieldViewport, frame }: PluginContext,
    anchor: Anchor,
    offset: FieldPoint,
    options?: MoveStartOptions
) {
    const option = frame.options?.find((opt) => opt.id === anchor.id);
    const defaultToMoveEnd = options?.defaultToMoveEnd;

    if (anchor.type === SpecialAnchorTypes.OPTION_START) {
        const sourceMove = frame.getMove(option.source);

        if (!sourceMove) {
            return;
        }

        const { distanceAlong } = findClosestPointAlongMove(sourceMove, offset);
        const sourceMoveLength = getMoveLength(sourceMove);
        const receiverMove = frame.getMove(option.target);
        const optionEnd = option.props?.end ?? sourceMoveLength;
        const newOptionEnd =
            receiverMove && distanceAlong > optionEnd
                ? Math.min(distanceAlong, getMoveLength(receiverMove))
                : optionEnd;

        api.updateOption({
            id: anchor.id,
            start: distanceAlong,
            end: newOptionEnd,
        });

        return;
    }

    const update: any = {
        id: anchor.id,
        type: ToolTypes.OPTION,
    };

    if (option) {
        update.source = option.source;
    } else if (frame.balls.length === 1) {
        const ball = frame.balls[0];
        update.source = ball.props.carrier;
    }

    if (typeof update.source !== 'number') {
        const closestCarrier = findClosestPiece(offset, frame.hasBall());
        update.source = closestCarrier.id;

        if (defaultToMoveEnd) {
            update.start = resolveMoveStart(frame, {
                playerId: update.source,
                defaultToMoveEnd,
            });
        }

        api.updateOption(update);
        return;
    }

    const { closestLineDistance, closestDistance, distanceAlong, closestMove, closestOption } =
        findClosestOptionPoint(
            getMoveLines(play.config, lines, frame.idx),
            fieldViewport,
            frame,
            offset,
            update.source
        );

    const onReceiverRoute = closestLineDistance < closestDistance;
    const targetPlayer = onReceiverRoute ? closestMove.target : closestOption?.id;
    if (typeof targetPlayer !== 'number') {
        return;
    }

    const receiverMove = frame.getMove(targetPlayer);
    const end = resolvePassEnd(onReceiverRoute, distanceAlong, Boolean(receiverMove));
    const start = resolveMoveStart(frame, {
        playerId: update.source,
        existingStart: option?.props?.start,
        defaultToMoveEnd,
        end,
    });

    update.target = targetPlayer;
    update.start = start;
    if (typeof end === 'number' && (onReceiverRoute || defaultToMoveEnd)) {
        update.end = end;
    }

    const duplicate = frame.options?.find(
        (opt) =>
            opt.source === update.source &&
            opt.target === update.target &&
            (typeof update.id !== 'number' || opt.id !== update.id)
    );

    if (duplicate) {
        api.removeMove(update);
    } else {
        api.updateOption(update);
    }
}

export function handleOptionDrag(
    { play, lines, api, fieldViewport, frame }: PluginContext,
    anchor: Anchor,
    offset: FieldPoint,
    options?: MoveStartOptions
) {
    const option = frame.options.find((opt) => opt.id === anchor.id);
    const defaultToMoveEnd = options?.defaultToMoveEnd;

    if (anchor.type === SpecialAnchorTypes.OPTION_START) {
        const sourceMove = frame.getMove(option.source);

        if (!sourceMove) {
            return;
        }

        const { distanceAlong } = findClosestPointAlongMove(sourceMove, offset);
        const sourceMoveLength = getMoveLength(sourceMove);
        const receiverMove = frame.getMove(option.target);
        const optionEnd = option?.props?.end ?? sourceMoveLength;
        const newOptionEnd =
            receiverMove && distanceAlong > optionEnd
                ? Math.min(distanceAlong, getMoveLength(receiverMove))
                : optionEnd;

        frame.updateOption(anchor.id, {
            origin: calculatePartialMovement(sourceMove, distanceAlong),
            endpoint: calculatePartialMovement(receiverMove, newOptionEnd),
        });
    } else {
        const {
            closestLineDistance,
            closestDistance,
            closestPoint,
            closestLine,
            closestOption,
            distanceAlong,
            closestMove,
        } = findClosestOptionPoint(
            getMoveLines(play.config, lines, frame.idx),
            fieldViewport,
            frame,
            offset,
            option.source
        );

        api.debug.points([
            offset,
            closestPoint ? fieldViewport.getFieldCoords(closestPoint) : null,
            closestLine ? fieldViewport.getFieldCoords(closestLine.a) : null,
            closestLine ? fieldViewport.getFieldCoords(closestLine.b) : null,
            ...(closestMove?.curved ? getCurvedMovePoints(closestMove) : []),
        ]);

        const onReceiverRoute = closestLineDistance < closestDistance;
        const targetPlayer = onReceiverRoute ? closestMove.target : closestOption?.id;
        if (typeof targetPlayer !== 'number') {
            return;
        }

        const sourceMove = frame.getMove(option.source);
        const receiverMove = frame.getMove(targetPlayer);
        const end = resolvePassEnd(onReceiverRoute, distanceAlong, Boolean(receiverMove));
        const start = resolveMoveStart(frame, {
            playerId: option.source,
            existingStart: option?.props?.start,
            defaultToMoveEnd,
            end,
        });

        frame.updateOption(anchor.id, {
            targetId: targetPlayer,
            endpoint: onReceiverRoute
                ? calculatePartialMovement(closestMove, end)
                : closestOption.origin,
            ...(sourceMove ? { origin: calculatePartialMovement(sourceMove, start) } : {}),
        });
    }
}

export function getDefaultOptionMove(
    { play, lines, fieldViewport, frame },
    type: string,
    offset: FieldPoint,
    options?: MoveStartOptions
) {
    const hasBall = frame.hasBall();
    const closestCarrier = findClosestPiece(offset, hasBall);

    if (!closestCarrier) {
        return null;
    }

    if (frame.balls.length > 1) {
        return defaultMove(frame.field, closestCarrier, type, closestCarrier.id);
    }

    const { closestLineDistance, closestDistance, closestOption, distanceAlong, closestMove } =
        findClosestOptionPoint(
            getMoveLines(play.config, lines, frame.idx),
            fieldViewport,
            frame,
            offset,
            closestCarrier.id
        );

    if (!closestOption) {
        return null;
    }

    const onReceiverRoute = closestLineDistance < closestDistance;
    const receiverMove = frame.getMove(closestOption.id);
    const end = resolvePassEnd(onReceiverRoute, distanceAlong, Boolean(receiverMove));
    const start = resolveMoveStart(frame, {
        playerId: closestCarrier.id,
        defaultToMoveEnd: options?.defaultToMoveEnd,
        end,
    });
    const sourceMove = frame.getMove(closestCarrier.id);
    const origin = sourceMove
        ? (calculatePartialMovement(sourceMove, start) ?? closestCarrier.origin)
        : closestCarrier.origin;
    const endpoint = onReceiverRoute
        ? calculatePartialMovement(closestMove, distanceAlong)
        : closestOption.origin;

    return new Move({
        type,
        target: closestCarrier.id,
        source: closestCarrier.id,
        origin,
        anchors: [endpoint],
    });
}

/**
 * Route options branch from a continuously-manipulatable point along another player's root move
 * (the same tether mechanic as {@link SpecialAnchorTypes.PASS_START}), then behave like a fully
 * independent movement past that point. Unlike options/passes, they never chain onward to another
 * player's route - there is no `target`, only a `source` player and a `start` distance along their move.
 */
export function generateRouteOptionAnchors({ play, frame }: Pick<PluginContext, 'play' | 'frame'>) {
    const anchors = [];

    if (!play.fieldSetup && frame.routeOptions) {
        anchors.push(
            ...frame.routeOptions.flatMap((ro) => {
                const roAnchors = [
                    {
                        type: SpecialAnchorTypes.ROUTE_OPTION_START,
                        id: ro.id,
                        ...ro.origin,
                        space: 'screen' as const,
                    },
                    ...ro.anchors.map((anc: any, idx: number) => ({
                        type: ToolTypes.ROUTE_OPTION,
                        id: idx,
                        target: ro.id,
                        ...anc,
                    })),
                ];

                if (ro.anchors.length === 1) {
                    roAnchors.push({
                        type: SpecialAnchorTypes.ROUTE_OPTION_ELBOW,
                        id: 0,
                        target: ro.id,
                        ...getMidpoint(ro.origin, ro.anchors[0]),
                        props: {
                            anchorCount: ro.anchors.length,
                        },
                    } as any);
                }

                return roAnchors;
            })
        );
    }

    return anchors;
}

export function generateRouteOptionSelectedAnchors(
    { play, frame }: Pick<PluginContext, 'play' | 'frame'>,
    selected
) {
    if (selected?.type !== ToolTypes.ROUTE_OPTION || play.fieldSetup || !frame.routeOptions) {
        return [];
    }

    const routeOption = frame.routeOptions.find((ro: any) => ro.id === selected.id);
    if (!routeOption) {
        return [];
    }

    return [
        // Sits on top of the source move's anchor at the same point - a lower distanceBias
        // (applied by the caller for all selected anchors) lets it win that tie so it stays
        // grabbable once the route option is selected.
        {
            type: SpecialAnchorTypes.ROUTE_OPTION_START,
            id: routeOption.id,
            ...routeOption.origin,
            space: 'screen' as const,
        },
        ...arraySlidingWindow([routeOption.origin, ...routeOption.anchors], (anchor, nextAnchor, idx) => ({
            type: SpecialAnchorTypes.ROUTE_OPTION_ELBOW,
            id: idx,
            target: routeOption.id,
            x: (anchor.x + nextAnchor.x) / 2,
            y: (anchor.y + nextAnchor.y) / 2,
            props: {
                anchorCount: routeOption.anchors.length,
            },
        })),
    ];
}

export function generateRouteOptionLines({ play, frame }: Pick<PluginContext, 'play' | 'frame'>) {
    if (!play.fieldSetup && frame.routeOptions) {
        return frame.routeOptions.flatMap(createLines);
    }

    return [];
}

type RouteOptionMoveOptions = { snapToAngleGrid?: boolean };

/** Snaps a dragged route-option point to the 45° angle grid when `snapToAngleGrid` is requested (football only). */
function snapRouteOptionAnchor(
    routeOption: any,
    anchor: Anchor,
    candidate: FieldPoint,
    fieldViewport: FieldViewport,
    options?: RouteOptionMoveOptions
): FieldPoint {
    if (
        !options?.snapToAngleGrid ||
        (anchor.type !== ToolTypes.ROUTE_OPTION && anchor.type !== SpecialAnchorTypes.ROUTE_OPTION_ELBOW)
    ) {
        return candidate;
    }

    const previous = getPreviousMoveAnchor(routeOption, anchor.id as number);

    return snapMoveEndpointToAngleGrid(previous, candidate, fieldViewport) ?? candidate;
}

export function handleRouteOptionDragEnd(
    { api, frame, fieldViewport }: PluginContext,
    anchor: Anchor,
    offset: FieldPoint,
    options?: RouteOptionMoveOptions
) {
    if (anchor.type === SpecialAnchorTypes.ROUTE_OPTION_START) {
        const routeOption = frame.routeOptions?.find((ro: any) => ro.id === anchor.id);
        const sourceMove = routeOption && frame.getMove(routeOption.source);

        if (!sourceMove) {
            return;
        }

        const { distanceAlong } = findClosestPointAlongMove(sourceMove, offset);

        api.updateRouteOption({
            id: anchor.id,
            start: distanceAlong,
        });

        return;
    }

    const routeOption = frame.routeOptions?.find((ro: any) => ro.id === anchor.target);
    if (!routeOption) {
        return;
    }

    const anchorCoords = snapRouteOptionAnchor(routeOption, anchor, offset, fieldViewport, options);

    const newRouteOption =
        anchor.type === SpecialAnchorTypes.ROUTE_OPTION_ELBOW && routeOption.anchors.length === anchor.props.anchorCount
            ? frame.insertRouteOptionAnchor(anchor.target, anchor.id, anchorCoords)
            : frame.updateRouteOptionAnchor(anchor.target, anchor.id, anchorCoords);

    api.updateRouteOption(newRouteOption);
}

export function handleRouteOptionDrag(
    { frame, fieldViewport }: PluginContext,
    anchor: Anchor,
    offset: FieldPoint,
    clamped: FieldPoint,
    options?: RouteOptionMoveOptions
) {
    if (anchor.type === SpecialAnchorTypes.ROUTE_OPTION_START) {
        const routeOption = frame.routeOptions?.find((ro: any) => ro.id === anchor.id);
        const sourceMove = routeOption && frame.getMove(routeOption.source);

        if (!sourceMove) {
            return;
        }

        const { distanceAlong } = findClosestPointAlongMove(sourceMove, offset);

        frame.updateRouteOption(anchor.id, {
            origin: calculatePartialMovement(sourceMove, distanceAlong),
        });

        return;
    }

    const routeOption = frame.routeOptions?.find((ro: any) => ro.id === anchor.target);
    if (!routeOption) {
        return;
    }

    const anchorCoords = snapRouteOptionAnchor(routeOption, anchor, clamped, fieldViewport, options);

    if (
        anchor.type === SpecialAnchorTypes.ROUTE_OPTION_ELBOW &&
        routeOption.anchors.length === anchor.props.anchorCount
    ) {
        frame.insertRouteOptionAnchor(anchor.target, anchor.id, anchorCoords);
    } else {
        frame.updateRouteOptionAnchor(anchor.target, anchor.id, anchorCoords);
    }
}

const getShadingDistanceBias = (piece: FieldPiece, fieldViewport: FieldViewport) => {
    if (typeof piece.props?.moveId === 'undefined') {
        const shortestDimension = Math.min(piece.props.width, piece.props.height);
        const pixelLength = fieldViewport.getPixelLength(shortestDimension);
        const bias = 30 / pixelLength;

        return bias;
    }

    return 1;
};

export function generateShadingAnchors({
    play,
    frame,
    fieldViewport,
}: Pick<PluginContext, 'play' | 'frame' | 'fieldViewport'>) {
    const anchors = [];

    if (play.field.pieces) {
        const shadings = play.field.pieces.filter(
            (piece) =>
                [ToolTypes.ELLIPSE, ToolTypes.RECTANGLE].includes(piece.type) &&
                (piece.props?.onAllFrames || piece.props?.frameIdx === frame.idx)
        );

        anchors.unshift(
            ...shadings.map((piece) => ({
                type: piece.type,
                id: piece.id,
                origin: piece.origin,
                ...piece.origin,
                distanceBias: getShadingDistanceBias(piece, fieldViewport),
                space: 'screen' as const,
            }))
        );
    }

    return anchors;
}

export function generateShadingSelectedAnchors({ fieldViewport }, selected: FieldPiece) {
    const anchors = [];

    if (![ToolTypes.ELLIPSE, ToolTypes.RECTANGLE].includes(selected.type)) {
        return [];
    }

    const base = {
        type: SpecialAnchorTypes.SHADING_RESIZE,
        id: selected.id,
        origin: selected.origin,
    };

    anchors.unshift(
        ...getShadingCorners(selected.origin, selected.props.width, selected.props.height).map(({ x, y }) => ({
            ...base,
            ...rotateAroundOrigin(selected.origin, { x, y }, selected.rotation ?? 0),
        }))
    );

    const rotatedHandle = rotateAroundOrigin(
        selected.origin,
        getShadingRotationHandle(selected.origin, selected.props.height, fieldViewport),
        selected.rotation ?? 0
    );

    anchors.unshift({
        type: SpecialAnchorTypes.SHADING_ROTATE,
        id: selected.id,
        ...rotatedHandle,
        origin: selected.origin,
    });

    return anchors;
}

export function handleShadingDragEnd(
    { api, frame, play, fieldViewport }: PluginContext,
    anchor: Anchor,
    offset: FieldPoint,
    clamped: FieldPoint
) {
    const piece = play.field.getFieldPiece(anchor.id);

    if (anchor.type === SpecialAnchorTypes.SHADING_RESIZE) {
        const fieldAnc = fieldViewport.getFieldCoords(anchor);
        const [origin, width, height] = resizeShading(piece, fieldAnc, clamped);

        api.updateFieldPiece({
            id: anchor.id,
            origin,
            width,
            height,
        });
    } else if (anchor.type === SpecialAnchorTypes.SHADING_ROTATE) {
        api.updateFieldPiece({
            id: anchor.id,
            rotation: angleBetweenPoints(piece.origin, offset) + 90,
        });
    } else if (piece?.props?.moveId) {
        return;
    } else {
        const update: any = {
            type: anchor.type,
            id: anchor.id,
            origin: clamped,
        };

        if (typeof anchor.id === 'undefined') {
            update.onAllFrames = play.fieldSetup;

            if (!update.onAllFrames) {
                update.frameIdx = frame.idx;
            }
        }

        api.updateFieldPiece(update);
    }
}

export function handleShadingDrag(
    { frame, fieldViewport }: PluginContext,
    anchor: Anchor,
    offset: FieldPoint,
    clamped: FieldPoint
) {
    const piece = frame.getFieldPiece(anchor.id);

    if (anchor.type === SpecialAnchorTypes.SHADING_RESIZE) {
        const fieldAnc = fieldViewport.getFieldCoords(anchor);
        const [origin, width, height] = resizeShading(piece, fieldAnc, clamped);

        frame.updateFieldPiece(anchor.id, origin, {
            width,
            height,
        });
    } else if (anchor.type === SpecialAnchorTypes.SHADING_ROTATE) {
        frame.rotateFieldPiece(anchor.id, angleBetweenPoints(piece.origin, offset) + 90);
    } else if (piece?.props?.moveId) {
        return;
    } else {
        frame.updateFieldPiece(anchor.id, offset);
    }
}
